import { useRef, type PointerEvent } from "react";
import { KEY, type TouchButton } from "../data/gamesData";

export type SendKey = (code: number, pressed: boolean) => void;

// Capture keeps pointerup coming even if the finger slides off; it can throw
// for a pointer that's already gone, which must not block the key press.
const capture = (e: PointerEvent<Element>) => {
  try {
    e.currentTarget.setPointerCapture(e.pointerId);
  } catch {
    /* pointer already released */
  }
};

// Released on pointerup/cancel (with capture) so a key never sticks down.
function HoldButton({ button, send, className }: { button: TouchButton; send: SendKey; className: string }) {
  const down = useRef(false);
  const press = (e: PointerEvent) => {
    e.preventDefault();
    capture(e);
    if (down.current) return;
    down.current = true;
    button.keys.forEach((k) => send(k, true));
  };
  const release = () => {
    if (!down.current) return;
    down.current = false;
    [...button.keys].reverse().forEach((k) => send(k, false));
  };
  return (
    <button
      type="button"
      onPointerDown={press}
      onPointerUp={release}
      onPointerCancel={release}
      onContextMenu={(e) => e.preventDefault()}
      className={`select-none touch-none active:bg-white/30 ${className}`}
    >
      {button.label}
    </button>
  );
}

// One pad for all four arrows: sliding the thumb switches direction (and
// diagonals) without lifting, like a real d-pad.
function DPad({ send }: { send: SendKey }) {
  const held = useRef(new Set<number>());

  const update = (next: Set<number>) => {
    held.current.forEach((k) => !next.has(k) && send(k, false));
    next.forEach((k) => !held.current.has(k) && send(k, true));
    held.current = next;
  };

  const track = (e: PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const dx = (e.clientX - r.left) / r.width - 0.5;
    const dy = (e.clientY - r.top) / r.height - 0.5;
    const t = 0.15; // dead zone around the center
    const next = new Set<number>();
    if (dy < -t) next.add(KEY.up);
    if (dy > t) next.add(KEY.down);
    if (dx < -t) next.add(KEY.left);
    if (dx > t) next.add(KEY.right);
    update(next);
  };

  return (
    <div
      onPointerDown={(e) => {
        e.preventDefault();
        capture(e);
        track(e);
      }}
      onPointerMove={(e) => e.buttons && track(e)}
      onPointerUp={() => update(new Set())}
      onPointerCancel={() => update(new Set())}
      onContextMenu={(e) => e.preventDefault()}
      className="relative h-40 w-40 shrink-0 select-none touch-none rounded-full border border-white/15 bg-white/5"
      aria-label="Direction pad"
    >
      <span className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 text-xl text-white/60">▲</span>
      <span className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 text-xl text-white/60">▼</span>
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-white/60">◀</span>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xl text-white/60">▶</span>
    </div>
  );
}

const SYSTEM: TouchButton[] = [
  { label: "Esc", keys: [KEY.esc] },
  { label: "Enter", keys: [KEY.enter] },
  { label: "Y", keys: [KEY.y] },
];

export default function TouchControls({
  actions,
  extras = [],
  send,
}: {
  actions: TouchButton[];
  extras?: TouchButton[];
  send: SendKey;
}) {
  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <DPad send={send} />
        <div className="grid grid-cols-2 gap-3">
          {actions.map((b) => (
            <HoldButton
              key={b.label}
              button={b}
              send={send}
              className="h-16 w-16 rounded-full border border-red-400/40 bg-red-700/60 text-sm font-semibold"
            />
          ))}
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {[...SYSTEM, ...extras].map((b) => (
          <HoldButton
            key={b.label}
            button={b}
            send={send}
            className="h-10 min-w-10 rounded-md border border-white/15 bg-white/10 px-3 text-sm"
          />
        ))}
      </div>
    </div>
  );
}
